import { useState, ChangeEvent, FormEvent } from 'react';
import { useToast } from '@/hooks/use-toast';
import { CSVUploadResponse, BulkUploadResponse, AsyncUploadResponse } from '@/types/upload';

type UploadResponse = CSVUploadResponse | BulkUploadResponse | AsyncUploadResponse;

interface UseCSVUploadProps<T extends UploadResponse> {
  uploadFn: (file: File, ...args: any[]) => Promise<T>;
  onSuccess?: (result: T) => void;
  onCancel?: () => void;
  autoCloseOnErrors?: boolean; // New prop to control if modal should close on errors
}

interface UseCSVUploadResult<T extends UploadResponse> {
  file: File | null;
  error: string | null;
  uploading: boolean;
  importResult: T | null; // New property to store the import result
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent, ...args: any[]) => Promise<void>;
  handleCancel: () => void;
  setError: (error: string | null) => void;
  resetResult: () => void; // New method to reset the result
}

export function useCSVUpload<T extends UploadResponse>({
  uploadFn,
  onSuccess,
  onCancel,
  autoCloseOnErrors = false // Default to false - don't close on errors
}: UseCSVUploadProps<T>): UseCSVUploadResult<T> {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<T | null>(null);

  const resetResult = () => {
    setImportResult(null);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Check file extension
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file.');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError(null);
      resetResult();
    }
  };

  const handleSubmit = async (e: FormEvent, ...args: any[]) => {
    // Prevent default form behavior
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Submit handler called for file upload');
    
    // File validation is now handled by the component using this hook
    // This allows components to decide when to validate (e.g., only on upload, not on export)
    
    try {
      setUploading(true);
      setError(null);
      resetResult();
      
      console.log(`Starting upload of file: ${file?.name}`);
      
      if (!file) {
        throw new Error('Please select a CSV file to upload');
      }
      
      const result = await uploadFn(file, ...args);
      console.log('Upload result:', result);
      
      setImportResult(result);
      
      // Check if this is a BulkUploadResponse (has errors property but no jobId)
      if ('errors' in result && !('jobId' in result) && result.errors && result.errors.length > 0) {
        // Show error toast but with success information as well
        toast({
          title: 'Partially imported',
          description: `Imported ${result.imported} items. ${result.errors.length} errors occurred.`,
          variant: 'destructive',
        });
        
        // Only call onSuccess and reset file if autoCloseOnErrors is true
        if (autoCloseOnErrors && onSuccess) {
          onSuccess(result);
          setFile(null);
        }
      } 
      // Check if this is an AsyncUploadResponse (has jobId but potentially no errors yet)
      else if ('jobId' in result) {
        toast({
          title: 'Upload started',
          description: result.message || 'Processing your file. Please wait for results.',
        });
        
        // Call success callback for async processing
        if (onSuccess) {
          onSuccess(result);
        }
      }
      // Otherwise, assume it's a successful synchronous upload
      else {
        toast({
          title: 'Success',
          description: `Successfully imported ${('imported' in result) ? result.imported : ''} items.`,
        });
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }
        
        // Reset form
        setFile(null);
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (!uploading) {
      setFile(null);
      setError(null);
      resetResult();
      if (onCancel) {
        onCancel();
      }
    }
  };

  return {
    file,
    error,
    uploading,
    importResult,
    handleFileChange,
    handleSubmit,
    handleCancel,
    setError,
    resetResult
  };
}
